import React, { useEffect, useState } from 'react';
import { ArrowRight, Check, Loader2, Menu, X } from 'lucide-react';
import '../App.css';
import { ContactFormData, submitContactForm } from '../lib/firebase';
import { employeeBand, initializeMarketingAnalytics, trackMarketingEvent } from '../lib/analytics';

const capabilities = [
  {
    number: '01',
    title: 'Attendance you can trust',
    copy: 'Record verified clock-ins from the field and keep a clean trail from shift start to approval.',
  },
  {
    number: '02',
    title: 'One workforce record',
    copy: 'Keep people, sites, devices and activity connected instead of reconciling separate spreadsheets.',
  },
  {
    number: '03',
    title: 'Payroll-ready hours',
    copy: 'Review exceptions early and hand finance a clear, auditable record at the end of each pay period.',
  },
];

const industries = [
  {
    name: 'Agriculture',
    detail: 'Seasonal teams and distributed farms',
    image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&q=85&w=1200',
  },
  {
    name: 'Construction',
    detail: 'Crews moving between active sites',
    image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=85&w=1200',
  },
  {
    name: 'Mining',
    detail: 'Shift-based, compliance-led operations',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&q=85&w=1200',
  },
  {
    name: 'Manufacturing',
    detail: 'High-volume teams and precise hours',
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=85&w=1200',
  },
];

const Home: React.FC = () => {
  useEffect(() => { initializeMarketingAnalytics(); }, []);
  const [employeeCount, setEmployeeCount] = useState(20);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    const result = await submitContactForm(formData);
    setSubmitting(false);
    setStatus(result.success ? 'success' : 'error');
    trackMarketingEvent(result.success ? 'enquiry_submitted' : 'enquiry_failed');
    if (result.success) {
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    }
  };

  const scrollTo = (id: string) => {
    trackMarketingEvent('section_opened', { section: id.slice(1) });
    setMenuOpen(false);
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const enterprisePricing = employeeCount > 1000;
  const customPricing = employeeCount > 500;
  const standardRate = employeeCount <= 25 ? 85 : employeeCount <= 100 ? 65 : 50;
  const plusRate = employeeCount <= 25 ? 120 : employeeCount <= 100 ? 90 : 65;
  const rand = (amount: number) => `R${amount.toLocaleString('en-ZA')}`;
  const enquireAboutPlan = (name: string) => {
    trackMarketingEvent('quote_requested', { plan: enterprisePricing ? 'Enterprise' : name, employee_band: employeeBand(employeeCount) });
    setFormData((data) => ({
      ...data,
      message: `I'm interested in ${enterprisePricing ? 'Enterprise' : name} for ${enterprisePricing ? 'more than 1,000' : employeeCount} employees. Please send me a quote.`,
    }));
    setStatus('idle');
    scrollTo('#contact');
  };

  return (
    <div className="public-site">
      <header className="public-nav">
        <a href="/" className="public-brand" aria-label="Waypoint Industries home">
          <img src="/logo.svg" alt="" />
          <span>Waypoint Industries</span>
        </a>

        <nav className={menuOpen ? 'public-links is-open' : 'public-links'} aria-label="Main navigation">
          <button onClick={() => scrollTo('#platform')}>Platform</button>
          <button onClick={() => scrollTo('#industries')}>Industries</button>
          <button onClick={() => scrollTo('#pricing')}>Pricing</button>
          <button onClick={() => scrollTo('#contact')}>Contact</button>
          <a href="/admin">Sign in</a>
        </nav>

        <button
          type="button"
          className="public-menu"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      <main>
        <section className="public-hero">
          <div className="public-eyebrow">Workforce operations · South Africa</div>
          <div className="public-hero-grid">
            <div>
              <h1>Know who worked.<br />Know what to pay.</h1>
            </div>
            <div className="public-hero-copy">
              <p>
                Waypoint connects field attendance, workforce records and payroll preparation for teams that work
                beyond the office.
              </p>
              <button type="button" className="public-primary" onClick={() => scrollTo('#contact')}>
                Talk to our team <ArrowRight size={18} />
              </button>
            </div>
          </div>
          <figure className="public-hero-media">
            <img
              src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&q=88&w=2200"
              alt="A field operations team working on an industrial site"
              fetchPriority="high"
            />
            <figcaption>
              <span>Field operations</span>
              <span>From first clock-in to approved hours</span>
            </figcaption>
          </figure>
          <div className="public-hero-strip" aria-label="Platform summary">
            <span>Built for field teams</span>
            <span>Verified attendance</span>
            <span>Clear audit trails</span>
          </div>
        </section>

        <section className="public-section" id="platform">
          <div className="public-section-intro">
            <p className="public-kicker">The platform</p>
            <h2>Less admin between the site and the payslip.</h2>
            <p>
              A focused operating layer for the work that usually gets lost between devices, paper registers and
              payroll files.
            </p>
          </div>
          <div className="public-capabilities">
            {capabilities.map((item) => (
              <article key={item.number}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="public-industries" id="industries">
          <div>
            <p className="public-kicker">Where we work</p>
            <h2>Designed around real sites, shifts and crews.</h2>
          </div>
          <div className="public-industry-grid">
            {industries.map((industry, index) => (
              <article key={industry.name}>
                <img src={industry.image} alt="" loading="lazy" />
                <div>
                  <span>0{index + 1}</span>
                  <h3>{industry.name}</h3>
                  <p>{industry.detail}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="public-pricing" id="pricing" aria-labelledby="pricing-title">
          <div className="public-section-intro">
            <p className="public-kicker">Pricing</p>
            <h2 id="pricing-title">Your team grows.<br />Your seat rate drops.</h2>
            <p>Monthly pricing per employee, in South African rand. Find your team size to compare both tiers.</p>
          </div>

          <div className="public-pricing-control">
            <div className="public-pricing-count">
              <label htmlFor="pricing-employees">How many employees?</label>
              <output htmlFor="pricing-employees">{enterprisePricing ? '1,000+' : employeeCount.toLocaleString('en-ZA')}<span> employees</span></output>
            </div>
            <input
              id="pricing-employees"
              className="public-pricing-slider"
              type="range"
              min={15}
              max={1001}
              step={1}
              value={employeeCount}
              aria-valuetext={`${employeeCount} employees${customPricing ? ', custom pricing' : ''}`}
              onChange={(event) => setEmployeeCount(Number(event.target.value))}
              onPointerUp={() => trackMarketingEvent('pricing_calculated', { employee_band: employeeBand(employeeCount) })}
              onKeyUp={(event) => {
                if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
                  trackMarketingEvent('pricing_calculated', { employee_band: employeeBand(employeeCount) });
                }
              }}
              style={{ '--slider-progress': `${((employeeCount - 15) / 986) * 100}%` } as React.CSSProperties}
            />
            <div className="public-pricing-range" aria-hidden="true"><span>15 employees</span><span>1,000+ employees</span></div>
          </div>

          <div className="public-pricing-plans">
            {[
              { name: 'Base', minimum: 20, rate: standardRate, bands: ['20–25', '26–100', '101–500'], rates: [85, 65, 50] },
              { name: 'Main', minimum: 15, rate: plusRate, bands: ['15–25', '26–100', '101–500'], rates: [120, 90, 65] },
            ].map((plan) => {
              const belowMinimum = employeeCount < plan.minimum;
              return (
                <article className="public-pricing-plan" key={plan.name} aria-label={`${plan.name} pricing`}>
                  <div className="public-pricing-plan-heading"><h3>{plan.name}</h3><span>{plan.minimum}-employee minimum</span></div>
                  <div className="public-pricing-quote" aria-live="polite" aria-atomic="true">
                    <p className="public-pricing-price">{enterprisePricing ? 'Enterprise' : customPricing ? 'Let’s talk' : rand(plan.rate)}{!customPricing && <span> / seat / month</span>}</p>
                    <p className="public-pricing-total">
                      {enterprisePricing ? 'A tailored quote for more than 1,000 employees.' : customPricing ? 'A negotiated rate for your workforce.' : belowMinimum ? `Available from ${plan.minimum} employees · ${rand(plan.minimum * plan.rate)} / month` : <><strong>{rand(employeeCount * plan.rate)} / month</strong> for {employeeCount} employees</>}
                    </p>
                  </div>
                  <dl className="public-pricing-bands">
                    {plan.bands.map((band, index) => (
                      <div key={band} className={!belowMinimum && !customPricing && plan.rate === plan.rates[index] ? 'is-active' : ''}>
                        <dt>{band} employees</dt><dd>{rand(plan.rates[index])}<span> / seat / mo</span></dd>
                      </div>
                    ))}
                    <div className={customPricing && !enterprisePricing ? 'is-active' : ''}><dt>501–1,000 employees</dt><dd>Negotiable</dd></div>
                    <div className={enterprisePricing ? 'is-active' : ''}><dt>Over 1,000 employees</dt><dd>Enterprise</dd></div>
                  </dl>
                  <button type="button" className="public-primary" onClick={() => enquireAboutPlan(plan.name)} disabled={belowMinimum}>
                    {belowMinimum ? `Minimum ${plan.minimum} employees` : 'Request a quote'}<ArrowRight size={18} />
                  </button>
                </article>
              );
            })}
          </div>
          <p className="public-pricing-note">Your team’s employee band determines the rate per seat. Teams of 501–1,000 receive a negotiated rate; larger teams receive an Enterprise quote.</p>
        </section>

        <section className="public-contact" id="contact">
          <div className="public-contact-copy">
            <p className="public-kicker">Start a conversation</p>
            <h2>Tell us how your workforce operates.</h2>
            <p>We’ll discuss your sites, current process and where Waypoint can remove manual work.</p>
            <a href="mailto:info@waypoint.industries">info@waypoint.industries</a>
          </div>

          <form onSubmit={submit} className="public-form">
            <div className="public-form-row">
              <label>
                Name
                <input
                  required
                  value={formData.name}
                  onChange={(event) => setFormData((data) => ({ ...data, name: event.target.value }))}
                />
              </label>
              <label>
                Work email
                <input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(event) => setFormData((data) => ({ ...data, email: event.target.value }))}
                />
              </label>
            </div>
            <label>
              Company
              <input
                value={formData.company}
                onChange={(event) => setFormData((data) => ({ ...data, company: event.target.value }))}
              />
            </label>
            <label>
              What would you like to improve?
              <textarea
                required
                rows={4}
                value={formData.message}
                onChange={(event) => setFormData((data) => ({ ...data, message: event.target.value }))}
              />
            </label>
            <div className="public-form-action" aria-live="polite">
              <button type="submit" disabled={submitting} className="public-primary">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Send enquiry'}
                {!submitting && <ArrowRight size={18} />}
              </button>
              {status === 'success' && <span className="public-success"><Check size={16} /> Message sent</span>}
              {status === 'error' && <span className="public-error">Could not send. Please try again.</span>}
            </div>
          </form>
        </section>
      </main>

      <footer className="public-footer">
        <span>Waypoint Industries</span>
        <span>Workforce operations, made clear.</span>
        <span>© {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
};

export default Home;
